import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, MailCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../lib/branding";

export default function Login() {
  const { login, verifyMfa, resendMfa, user, ready } = useAuth();
  const navigate = useNavigate();
  const branding = useBranding();
  // Steps: "credentials" -> "verify"
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // MFA state
  const [challengeId, setChallengeId] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [code, setCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(true);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputRef = useRef(null);

  useEffect(() => {
    if (step === "verify") setTimeout(() => codeInputRef.current?.focus(), 50);
  }, [step]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  if (ready && user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.ok) { setErr(res.error); return; }
    if (res.mfaRequired) {
      setChallengeId(res.challengeId);
      setEmailHint(res.emailHint || "your inbox");
      setResendCooldown(30);
      setStep("verify");
      return;
    }
    navigate("/");
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setErr("");
    if (!/^\d{6}$/.test(code)) { setErr("Enter the 6-digit code from your email"); return; }
    setLoading(true);
    const res = await verifyMfa(challengeId, code, rememberDevice);
    setLoading(false);
    if (!res.ok) { setErr(res.error); return; }
    navigate("/");
  };

  const resend = async () => {
    setErr("");
    const res = await resendMfa(challengeId);
    if (!res.ok) { setErr(res.error); return; }
    setResendCooldown(30);
  };

  // Split the tagline at "A CRM" to mirror the mock's two-line break
  const taglineParts = (() => {
    const t = branding.tagline || "";
    const marker = "A CRM";
    const i = t.indexOf(marker);
    if (i <= 0) return [t, ""];
    return [t.slice(0, i).replace(/\s+$/, ""), t.slice(i)];
  })();

  return (
    <div className="auth-split" data-testid="login-shell">
      {/* ─── Left (white) — form ─── */}
      <div className="auth-split-left">
        <div className="auth-split-logo">
          <img
            src={branding.logo_data_url || "/pragma-login-logo-v4.png"}
            alt="Pragma"
            data-testid="login-brand-logo"
          />
        </div>

        <div className="auth-split-form-wrap">
          {step === "credentials" ? (
            <>
              <h1 className="auth-split-h1" data-testid="login-heading">Welcome back!</h1>
              <p className="auth-split-sub">Login to your PragmaCRM account.</p>

              <form onSubmit={submit} data-testid="login-form">
                <input
                  id="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="auth-split-input"
                  placeholder="Email address"
                  data-testid="login-email"
                  required
                  autoComplete="email"
                  aria-label="Email address"
                />
                <input
                  id="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="auth-split-input"
                  placeholder="Password"
                  data-testid="login-password"
                  required
                  autoComplete="current-password"
                  aria-label="Password"
                />
                {err && <div className="auth-split-error" data-testid="login-error">{err}</div>}
                <div className="auth-split-actions">
                  <Link to="/forgot-password" className="auth-split-secondary" data-testid="login-forgot-link">
                    Forgot your password?
                  </Link>
                  <button type="submit" className="auth-split-submit" disabled={loading} data-testid="login-submit">
                    {loading ? "Logging in…" : "Login"}
                    <ArrowRight strokeWidth={2.2} />
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <h1 className="auth-split-h1" data-testid="mfa-heading">Verify it's you</h1>
              <p className="auth-split-sub">
                <MailCheck size={14} style={{ display: "inline", verticalAlign: -2, marginRight: 6, color: "var(--pg-accent-deep)" }} />
                We sent a 6-digit code to <strong>{emailHint}</strong>. It expires in 10 minutes.
              </p>

              <form onSubmit={submitCode} data-testid="mfa-form">
                <input
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="auth-split-input"
                  placeholder="000000"
                  data-testid="mfa-code-input"
                  style={{
                    fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace",
                    letterSpacing: "0.36em",
                    textAlign: "center",
                    fontSize: 22,
                  }}
                  aria-label="6-digit verification code"
                  required
                />

                <label style={{
                  display: "flex", alignItems: "center", gap: 8, marginTop: 4, marginBottom: 8,
                  fontSize: 13, color: "var(--pg-text-mute)", cursor: "pointer",
                }} data-testid="mfa-remember-label">
                  <input
                    type="checkbox"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                    data-testid="mfa-remember-checkbox"
                  />
                  Trust this device for 14 days
                </label>

                {err && <div className="auth-split-error" data-testid="mfa-error">{err}</div>}

                <div className="auth-split-actions">
                  <button
                    type="button"
                    className="auth-split-secondary"
                    onClick={() => { setStep("credentials"); setCode(""); setErr(""); }}
                    data-testid="mfa-back"
                  >
                    <ArrowLeft size={14} style={{ display: "inline", verticalAlign: -2, marginRight: 4 }} />
                    Back
                  </button>
                  <button
                    type="submit"
                    className="auth-split-submit"
                    disabled={loading || code.length !== 6}
                    data-testid="mfa-submit"
                  >
                    {loading ? "Verifying…" : "Verify & sign in"}
                    <ArrowRight strokeWidth={2.2} />
                  </button>
                </div>

                <div style={{ marginTop: 16, fontSize: 13, color: "var(--pg-text-mute)" }}>
                  Didn't get it?{" "}
                  {resendCooldown > 0 ? (
                    <span data-testid="mfa-resend-cooldown">Resend in {resendCooldown}s</span>
                  ) : (
                    <button
                      type="button"
                      onClick={resend}
                      className="auth-split-secondary"
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                      data-testid="mfa-resend"
                    >
                      Resend code
                    </button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>

        <div className="auth-split-footer" data-testid="login-signup-footer">
          Don't have an account?{" "}
          <Link to="/signup" data-testid="login-signup-link">Create one</Link>
        </div>
      </div>

      {/* ─── Right (dark) — marketing copy ─── */}
      <div className="auth-split-right" data-testid="login-marketing">
        <h2 className="auth-split-right-h1">
          {branding.heading_line_1}
          <br />
          <span className="accent">{branding.heading_line_2}</span>
        </h2>
        <p className="auth-split-right-sub">
          {taglineParts[0]}
          {taglineParts[1] && (
            <>
              <br />
              {taglineParts[1]}
            </>
          )}
        </p>
      </div>
    </div>
  );
}
